<template>
  <div v-if="configurationGroup" id="Buttons" :class="[configuratorOpen ? 'open' : 'closed']">
    <div class="top-row" :class="{ bordered: showDivider }" @click="onConfiguratorClick">
      <div class="name-row">
        <span class="title">{{ configurationGroup.name }}</span>
        <span v-if="isStackedConfigurator && displayed" class="select-title">{{ displayed.value }}</span>
        <span v-else-if="isStackedConfigurator && selected" class="select-title">{{ selected.value }}</span>
      </div>
      <span v-if="isStackedConfigurator" class="af af-plus config-toggle" :class="[configuratorOpen ? 'open' : 'closed']"></span>
    </div>
    <div ref="configuratorOptions">
      <transition name="configurator-value-container">
        <div
          v-if="configurationGroup.configuration.length < 5 && ((isStackedConfigurator && configuratorOpen) || !isStackedConfigurator)"
          ref="buttonRows"
          class="value-rows"
          :class="[
            (mobileSmallRows || isStackedConfigurator) && 'mobile-small-rows',
            configurationGroup.configuration.length == 1 && 'cursor-arrow',
            showDivider && 'bordered',
          ]"
        >
          <div v-for="(config, index) of configurationGroup.configuration" :key="index" class="btn-container">
            <button
              class="grey small"
              :class="[
                selected && selected.valueId === config.valueId && 'active',
                isStackedConfigurator && 'thick-configurator-button',
                selected && selected.valueId === config.valueId && 'filled',
                !selected || selected.valueId !== config.valueId && 'outlined',
              ]"
              @click.passive="onOptionChange($event, config)"
              @mouseover="onCheckHover($event, config)"
              @mouseout="onCheckMouseOut()"
            >
              {{ config.value }}
            </button>
          </div>
        </div>
        <div v-else-if="(isStackedConfigurator && configuratorOpen) || !isStackedConfigurator" class="dropdown-container">
          <Dropdown v-model="selected" :options="configurationGroup.configuration" :width="dropdownWidth" @input="onOptionChange($event, selected)"></Dropdown>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import { debounce } from 'lodash-es';
import smoothReflow from 'vue-smooth-reflow';
import { MediaUtils } from '@article/pixel';

import Dropdown from './Dropdown.vue';

export default {
  name: 'Buttons',
  components: {
    Dropdown,
  },
  mixins: [smoothReflow],
  props: {
    configurationGroup: Object,
    configuratorId: Number,
    isStackedConfigurator: {
      type: Boolean,
      default: false,
    },
    showDivider: Boolean,
  },
  data() {
    return {
      selected: null,
      dropdownWidth: '240px',
      mobileSmallRows: null,
      displayed: null,
    };
  },
  computed: {
    ...mapState('product', [
      'currentOpenConfigurator', //
    ]),
    ...mapGetters('product', [
      'configurationsLength'
    ]),
    configuratorOpen() {
      return this.configuratorId === this.currentOpenConfigurator;
    },
  },
  watch: {
    configurationGroup: {
      deep: true,
      immediate: true,
      handler() {
        this.formatSelected();
      },
    },
  },
  mounted() {
    this.setDropdownWidth();
    this.calcButtonHeight();
    window.addEventListener('resize', this.onWindowResizeDebounce);

    MediaUtils.isTabletAndGreater &&
      this.$smoothReflow({
        el: this.$refs.configuratorOptions,
        property: 'height',
        transition: 'height 150ms ease-in-out',
      });
  },
  destroyed() {
    window.removeEventListener('resize', this.onWindowResizeDebounce);
  },
  methods: {
    ...mapMutations('product', [
      'updateCurrentOpenConfigurator', //
    ]),
    formatSelected() {
      if(this.configurationGroup) {
        this.selected = this.configurationGroup.configuration.filter((value) => value.selected)[0];
      } else {
        this.selected = null;
      }
    },
    onOptionChange(event, value) {
      if (this.selected && this.selected.valueId !== value.valueId) {
        this.$emit('input', {
          id: this.configurationGroup.attributeId,
          configurationGroup: this.configurationGroup,
          value,
        });
        this.displayed = null;
      }
    },
    setDropdownWidth() {
      this.dropdownWidth = MediaUtils.isTabletAndSmaller ? '100%' : '240px';
    },
    calcButtonHeight() {
      if (MediaUtils.isMobileAndGreater) return;

      const { buttonRows } = this.$refs;
      if (buttonRows) {
        this.mobileSmallRows = this.configurationGroup.configuration.some((value) => value.value.length > 8);
      }
    },
    onWindowResize() {
      this.setDropdownWidth();
      this.calcButtonHeight();
    },
    onWindowResizeDebounce() {
      debounce(this.onWindowResize, 500)();
    },
    onConfiguratorClick() {
      if (this.isStackedConfigurator) {
        this.updateCurrentOpenConfigurator(this.configuratorId);
      }
    },
    onCheckHover(event, value) {
      this.displayed = { ...value };
    },
    onCheckMouseOut() {
      this.displayed = null;
    },
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';

#Buttons {
  display: flex;
  flex-direction: column;
  z-index: 10;

  .top-row {
    display: flex;
    justify-content: space-between;
  }

  .name-row {
    display: flex;

    .title {
      text-transform: uppercase;
      letter-spacing: var(--uppercase-letter-spacing);
      font-size: var(--font-size-14);
    }

    .select-title {
      font-family: var(--font-light);
      font-size: var(--font-size-14);
    }

    @media (min-width: $mobile-breakpoint) {
      margin-bottom: 8px;
    }
  }

  .value-rows {
    display: flex;
    flex-wrap: wrap;

    &.cursor-arrow {
      button {
        cursor: initial;
      }
    }

    .btn-container {
      button {
        &:not(:last-child) {
          margin-right: 8px;
        }
      }

      @media (min-width: $extra-small-breakpoint) and (max-width: $mobile-breakpoint - 1) {
        margin-top: 4px;

        button {
          width: 100%;
        }
      }

      @media (min-width: $small-breakpoint) and (max-width: $mobile-breakpoint - 1) {
        max-width: 33%;
        margin-top: 2px;
        margin-bottom: 4px;

        &:not(:last-child) {
          margin-right: 4px;
        }
      }

      @media (min-width: $mobile-breakpoint) {
        margin-right: 6px;
        margin-bottom: 4px;
      }

      @media (min-width: $desktop-breakpoint) {
        margin-right: 8px;
        margin-bottom: 4px;
      }
    }

    &.mobile-small-rows {
      .btn-container {
        @media (max-width: $small-breakpoint - 1) {
          flex: 1 0 47%;
          max-width: 47%;
          margin-bottom: 4px;

          &:nth-child(2n + 1) {
            margin-right: 4px;
          }

          &:nth-child(2n + 2) {
            margin-left: 4px;
          }
        }
      }
    }

    &:not(.mobile-small-rows) {
      .btn-container {
        @media (max-width: $small-breakpoint - 1) {
          &:nth-child(3n + 2) {
            margin-left: 4px;
            margin-right: 4px;
          }
        }

        @media (min-width: $extra-small-breakpoint) and (max-width: $mobile-breakpoint - 1) {
          max-width: fit-content;
        }

        @media (min-width: $extra-small-breakpoint) and (max-width: $small-breakpoint - 1) {
          flex: 1 0 32%;

          &:nth-child(2) {
            margin-left: 4px;
            margin-right: 4px;
          }

          &:nth-child(n + 4) {
            margin-top: 12px;
          }
        }
      }
    }
  }
}
</style>
